import classNames from 'classnames';
import { useSearchParams } from 'next/navigation';
import React, { useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import RichText from '@/components/atoms/rich-text/rich-text';
import Spinner from '@/components/atoms/spinner/spinner';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import ProfilePicture from '@/components/molecules/profile-picture/profile-picture';
import Tooltip from '@/components/molecules/tooltip/tooltip';
import IndividualTravelerCard from '@/components/organisms/individual-traveler-card/individual-traveler-card';
import IndividualTravelerOption, {
  IndividualTravelerOptionItem,
} from '@/components/organisms/individual-traveler-option/individual-traveler-option';
import GridLayout from '@/components/utilities/grid-layout/grid-layout';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import useMutationClaimInvitation from '@/integrations/content/users/claim-invitation/claim-invitation.hooks';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './accept-trip-invitation-modal.module.scss';

export interface AcceptTripInvitationModalProps {
  /** The class name to apply */
  className?: string;
  /** The description of the modal */
  description?: Content.Components['InvitationTripDto']['overlayDescription'];
  /** The encrypted booking hash */
  encryptedBookingHash?: Content.Components['InvitationTripDto']['encryptedBookingHash'];
  /** The headline of the modal */
  headline?: Content.Components['InvitationTripDto']['overlayHeadline'];
  /** Indicates whether the modal is open */
  isOpen?: boolean;
  /** The callback to handle the close event */
  onClose?: () => void;
  /** The id of the owner */
  ownerId?: Content.Components['InvitationTripDto']['ownerId'];
  /** The co-travelers to show */
  passengers?: Content.Components['InvitationTripDto']['passengers'];
  /** The pre headline of the modal */
  preHeadline?: Content.Components['InvitationTripDto']['overlayPreHeadline'];
  /** The overlay tooltip text */
  tooltipText: Content.Components['InvitationTripDto']['overlayTooltipText'];
}

const AcceptTripInvitationModal = ({
  className,
  description,
  encryptedBookingHash,
  headline,
  isOpen = false,
  onClose,
  ownerId,
  passengers,
  preHeadline,
  tooltipText,
}: AcceptTripInvitationModalProps) => {
  const { t } = useI18n();
  const searchParams = useSearchParams();

  const [selectedTravelerId, setSelectedTravelerId] = useState<string>('');

  const code = searchParams!.get('code') ?? '';

  const passengerList = passengers ?? [];
  const customer = passengerList.find(passenger => passenger.isAdmin);
  const coTravelers = passengerList.filter(passenger => !passenger.isAdmin);
  const adultCoTravelers = coTravelers.filter(coTraveler =>
    ['male', 'female'].includes(coTraveler.gender),
  );
  const childrenCoTravelers = coTravelers.filter(coTraveler =>
    ['child', 'infant'].includes(coTraveler.gender),
  );

  const adultCoTravelerOptionItems: IndividualTravelerOptionItem[] =
    adultCoTravelers.map(passenger => ({
      disabled:
        passenger.status === 'accepted' || passenger.status === 'notInvited',
      firstName: passenger.firstName,
      imageUrl: passenger.imageUrl,
      isSelected: passenger.uniquePaxHash === selectedTravelerId,
      lastName: passenger.lastName,
      uniquePaxHash: passenger.uniquePaxHash,
    }));

  const childrenCoTravelerOptionItems: IndividualTravelerOptionItem[] =
    childrenCoTravelers.map(passenger => ({
      firstName: passenger.firstName,
      imageUrl: passenger.imageUrl,
      lastName: passenger.lastName,
      uniquePaxHash: passenger.uniquePaxHash,
    }));

  const {
    isLoading: isMutatingClaimInvitation,
    mutateAsync: mutateAsyncClaimInvitation,
  } = useMutationClaimInvitation();

  const shouldShowSpinner = isMutatingClaimInvitation;

  const handleCloseModal = () => {
    onClose?.();
  };

  const handleRefreshPage = () => {
    if (!window) return;

    window.location.reload();
  };

  const handleAcceptInvitation = async () => {
    await mutateAsyncClaimInvitation({
      parameters: {
        code: code ?? '',
        encryptedBookingHash: encryptedBookingHash ?? '',
        ownerId: ownerId ?? '',
        passengerHash: selectedTravelerId ?? '',
      },
    });

    handleRefreshPage();
  };

  return (
    <Lightbox
      className={classNames(styles.component, className)}
      isCentered
      isFullWidthOnMobile
      isOpen={isOpen}
      onClose={handleCloseModal}
      size="small"
      theme="neutral"
      wrapperClassName={styles.lightboxWrapper}
    >
      <div className={classNames(styles.lightboxInnerWrapper)}>
        <div className={styles.contentWrapper}>
          {shouldShowSpinner && (
            <Spinner
              className={styles.spinner}
              hasTransparentOverlay
              loadingText={`${t('items.profile.profilePage.coTravelers.loadingText')}...`}
            />
          )}

          <div className={styles.topSection}>
            <div className={styles.header}>
              {preHeadline && (
                <Typography
                  className={styles.preHeadline}
                  tagStyle="labelMedium"
                >
                  {preHeadline}
                </Typography>
              )}

              {headline && (
                <Typography
                  className={styles.headline}
                  tagStyle="headingMedium"
                >
                  {headline}
                </Typography>
              )}

              {description && (
                <RichText className={styles.description} text={description} />
              )}
            </div>

            <div className={styles.travelersWrapper}>
              <div className={styles.adultTravelers}>
                {customer && (
                  <div className={styles.customer}>
                    <ProfilePicture
                      firstName={customer.firstName}
                      imageUrl={customer.imageUrl ?? undefined}
                      initialsTagStyle="labelLarge"
                      lastName={customer.lastName}
                      mode="invitation"
                      size={48}
                    />

                    <div className={styles.customerDetails}>
                      <Typography
                        className={styles.travelPlanner}
                        tagStyle="bodySmall"
                      >
                        {t(
                          'items.profile.profilePage.coTravelers.travelPlanner',
                        )}
                      </Typography>

                      <Typography
                        className={styles.fullName}
                        tagStyle="bodyMediumBold"
                      >
                        {`${customer.firstName} ${customer.lastName}`}
                      </Typography>
                    </div>
                  </div>
                )}

                <GridLayout
                  backgroundColor="surface"
                  borderColor="neutral"
                  threeColumnAt={800}
                  twoColumnAt={500}
                >
                  {adultCoTravelerOptionItems.map(option => (
                    <IndividualTravelerOption
                      key={option.uniquePaxHash}
                      onSelect={value =>
                        setSelectedTravelerId(previous =>
                          previous === value ? '' : value,
                        )
                      }
                      option={option}
                    />
                  ))}
                </GridLayout>
              </div>

              {childrenCoTravelers.length > 0 && (
                <div className={styles.childrenSection}>
                  <div className={styles.childrenSectionHeader}>
                    <Typography tagStyle="bodyMedium">
                      {`${t('items.profile.profilePage.coTravelers.addedChildrenInformation')}`}
                    </Typography>

                    {tooltipText && (
                      <Tooltip
                        content={
                          <RichText
                            className={styles.tooltipText}
                            text={tooltipText}
                          />
                        }
                        useMinimumWidth={false}
                        wrapChildrenOnly
                      >
                        <Icon
                          className={styles.tooltipIcon}
                          name="Info"
                          size={16}
                        />
                      </Tooltip>
                    )}
                  </div>

                  <div className={styles.childrenTravelers}>
                    <GridLayout
                      backgroundColor="surface"
                      borderColor="neutral"
                      columnType="static"
                      threeColumnAt={800}
                      twoColumnAt={500}
                    >
                      {childrenCoTravelerOptionItems.map(traveler => (
                        <IndividualTravelerCard
                          key={traveler.uniquePaxHash}
                          traveler={traveler}
                        />
                      ))}
                    </GridLayout>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.bottomSection}>
            <Button
              className={styles.continueButton}
              disabled={!selectedTravelerId}
              onClick={() => handleAcceptInvitation()}
              text={t('items.profile.profilePage.coTravelers.continue')}
              variant="primary"
            />
          </div>
        </div>
      </div>
    </Lightbox>
  );
};

AcceptTripInvitationModal.displayName = 'AcceptTripInvitationModal';

export default AcceptTripInvitationModal;
